import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Share2, X } from 'lucide-react';

const BrochurePage = () => {
  const [currentView, setCurrentView] = useState('front'); // 'front' or 'back'
  const [isFullscreen, setIsFullscreen] = useState(false);

  const frontImage = "https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/22g7t9cu_IMG_3439.png";
  const backImage = "https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/7q8kta8e_IMG_3440.png";

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Valenrock™ Home Services Brochure',
          text: 'Check out Valenrock™ Home Services - Whole-Home Assessments & Care Plans in Northern NJ',
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-[#003366] pt-20">
      {/* Header */}
      <div className="bg-[#003366] py-10 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/oryj1o1m_Transparent%20Png-04.png" 
            alt="Valenrock Home Services" 
            className="h-48 md:h-64 w-auto mx-auto mb-4"
          />
          <p className="text-[#CFB973] text-sm md:text-base">
            Digital Brochure • Whole-Home Assessments & Care Plans
          </p>
        </div>
      </div>

      {/* Brochure Viewer */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="flex justify-center gap-4 mb-6">
          <button
            onClick={() => setCurrentView('front')}
            className={`px-6 py-2 rounded-full font-semibold transition-all ${
              currentView === 'front'
                ? 'bg-[#CFB973] text-[#003366]'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            Front Side
          </button>
          <button
            onClick={() => setCurrentView('back')}
            className={`px-6 py-2 rounded-full font-semibold transition-all ${
              currentView === 'back'
                ? 'bg-[#CFB973] text-[#003366]'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            Back Side
          </button>
        </div>

        {/* Brochure Image Container */}
        <div className="relative">
          {/* Navigation Arrows - Desktop */}
          <button
            onClick={() => setCurrentView(currentView === 'front' ? 'back' : 'front')}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 w-10 h-10 bg-[#CFB973] rounded-full items-center justify-center text-[#003366] hover:bg-[#CFB973]/80 transition-colors z-10"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={() => setCurrentView(currentView === 'front' ? 'back' : 'front')}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 w-10 h-10 bg-[#CFB973] rounded-full items-center justify-center text-[#003366] hover:bg-[#CFB973]/80 transition-colors z-10"
          >
            <ChevronRight size={24} />
          </button>

          {/* Brochure Image */}
          <div 
            className="relative bg-white rounded-lg shadow-2xl overflow-hidden cursor-pointer"
            onClick={() => setIsFullscreen(true)}
          >
            <img
              src={currentView === 'front' ? frontImage : backImage}
              alt={`Valenrock Brochure - ${currentView === 'front' ? 'Front' : 'Back'}`}
              className="w-full h-auto transition-opacity duration-300"
            />
            
            {/* Clickable Hotspots - Front Side */}
            {currentView === 'front' && (
              <>
                {/* Phone Number Hotspot */}
                <a
                  href="tel:5513799774"
                  className="absolute bottom-[12%] left-[3%] w-[22%] h-[3%] cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                  title="Call (551) 379-9774"
                />
                {/* Email Hotspot */}
                <a
                  href="mailto:contact@valenrockhome.services"
                  className="absolute bottom-[9%] left-[3%] w-[28%] h-[3%] cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                  title="Email contact@valenrockhome.services"
                />
                {/* Website Hotspot */}
                <a
                  href="https://valenrockhome.services"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-[6%] left-[3%] w-[22%] h-[3%] cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                  title="Visit valenrockhome.services"
                />
                {/* QR Code Hotspot */}
                <a
                  href="https://valenrockhome.services/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-[15%] left-[3%] w-[18%] h-[18%] cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                  title="Schedule your Home Assessment"
                />
              </>
            )}

            {/* Clickable Hotspots - Back Side */}
            {currentView === 'back' && (
              <>
                {/* Book Assessment Button */}
                <a
                  href="https://valenrockhome.services/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-[5%] left-[3%] w-[30%] h-[5%] cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                  title="Book your Whole-Home Assessment"
                />
                {/* Learn More Button */}
                <a
                  href="https://valenrockhome.services"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-[8%] right-[3%] w-[28%] h-[8%] cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                  title="Learn More at valenrockhome.services"
                />
              </>
            )}

            {/* Tap to Zoom Overlay */}
            <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
              <span className="bg-black/50 text-white px-4 py-2 rounded-full text-sm">
                Tap to view fullscreen
              </span>
            </div>
          </div>
        </div>

        {/* Mobile Swipe Indicator */}
        <div className="flex justify-center items-center gap-2 mt-4 md:hidden">
          <div className={`w-2 h-2 rounded-full ${currentView === 'front' ? 'bg-[#CFB973]' : 'bg-white/30'}`} />
          <div className={`w-2 h-2 rounded-full ${currentView === 'back' ? 'bg-[#CFB973]' : 'bg-white/30'}`} />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <button
            onClick={handleShare}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            <Share2 size={20} />
            Share Brochure
          </button>
          <a
            href={currentView === 'front' ? frontImage : backImage}
            download={`Valenrock™-Brochure-${currentView}.png`}
            className="flex items-center gap-2 bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            <Download size={20} />
            Download Image
          </a>
        </div>

        {/* Quick Contact */}
        <div className="mt-12 bg-white/5 rounded-xl p-6 text-center">
          <h3 className="text-white font-bold text-lg mb-4">Ready to Get Started?</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:5513799774"
              className="flex items-center gap-2 text-[#CFB973] hover:text-white transition-colors"
            >
              📞 (551) 379-9774
            </a>
            <a
              href="mailto:contact@valenrockhome.services"
              className="flex items-center gap-2 text-[#CFB973] hover:text-white transition-colors"
            >
              ✉️ contact@valenrockhome.services
            </a>
          </div>
          <p className="text-white/60 text-sm mt-4">
            Assessments start at $249 • No subscriptions • No pressure
          </p>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setIsFullscreen(false)}
        >
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white z-50"
          >
            <X size={24} />
          </button>
          
          {/* Navigation in Fullscreen */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentView(currentView === 'front' ? 'back' : 'front');
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentView(currentView === 'front' ? 'back' : 'front');
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
          >
            <ChevronRight size={28} />
          </button>

          <img
            src={currentView === 'front' ? frontImage : backImage}
            alt={`Valenrock Brochure - ${currentView === 'front' ? 'Front' : 'Back'}`}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Page Indicator */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            <div className={`w-3 h-3 rounded-full ${currentView === 'front' ? 'bg-[#CFB973]' : 'bg-white/30'}`} />
            <div className={`w-3 h-3 rounded-full ${currentView === 'back' ? 'bg-[#CFB973]' : 'bg-white/30'}`} />
          </div>
        </div>
      )}
    </div>
  );
};

export default BrochurePage;
